import { useEffect, useMemo, useState } from "react";
import { Eye, Heart, BarChart3, RefreshCcw, Search } from "lucide-react";
import { api } from "@/lib/auth";
import { toast } from "sonner";

/* Friendly names for legacy / hardcoded vendors so the table reads nicely
 * instead of showing raw slugs for non-DB vendors. */
const LEGACY_NAMES = {
  "dj-papz": "DJ Papz",
  "dj-nitu": "DJ Nitu",
  "sooriya-production-services": "Sooriya Production Services",
  asha: "Stories by Asha",
};

const Stat = ({ label, value, Icon, tone = "ink" }) => {
  const toneClass =
    tone === "gold"
      ? "border-[var(--ld-gold-deep)]/30 text-[var(--ld-gold-deep)]"
      : tone === "rose"
      ? "border-rose-400/40 text-rose-500"
      : "border-[var(--ld-ink)]/15 text-[var(--ld-ink)]";
  return (
    <div className={`border ${toneClass} bg-[var(--ld-ivory)]/30 p-5`}>
      <div className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase opacity-70">
        <Icon size={12} strokeWidth={1.7} />
        {label}
      </div>
      <div className="mt-3 font-serif-display text-3xl text-[var(--ld-ink)]">
        {value}
      </div>
    </div>
  );
};

const VendorStatsPanel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/vendor-stats");
      setRows(r.data || []);
    } catch (e) {
      toast("Couldn't load vendor stats", { duration: 2400 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const decorated = useMemo(
    () =>
      rows.map((r) => ({
        ...r,
        displayName:
          r.source === "legacy" && LEGACY_NAMES[r.slug]
            ? LEGACY_NAMES[r.slug]
            : r.name,
      })),
    [rows]
  );

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return decorated;
    return decorated.filter((r) =>
      (r.displayName || "").toLowerCase().includes(term)
    );
  }, [decorated, q]);

  const totals = useMemo(
    () => ({
      vendors: decorated.length,
      impressions: decorated.reduce((s, r) => s + (r.impressions || 0), 0),
      views: decorated.reduce((s, r) => s + (r.profile_views || 0), 0),
      favorites: decorated.reduce((s, r) => s + (r.favorites || 0), 0),
    }),
    [decorated]
  );

  return (
    <div data-testid="section-vendor-stats">
      <div className="flex items-end justify-between gap-4">
        <div>
          <span className="font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
            Analytics
          </span>
          <h2 className="font-serif-display mt-3 text-3xl sm:text-4xl text-[var(--ld-ink)] font-medium leading-tight">
            Vendor Stats
          </h2>
          <p className="mt-2 text-sm text-[var(--ld-ink-muted)] max-w-xl">
            Live engagement per vendor — impressions on category pages, profile
            views, and couple favourites.
          </p>
        </div>
        <button
          data-testid="vendor-stats-refresh"
          onClick={load}
          className="inline-flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)] transition-colors"
        >
          <RefreshCcw size={14} strokeWidth={1.6} />
          Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Vendors tracked" value={totals.vendors} Icon={BarChart3} />
        <Stat label="Impressions" value={totals.impressions} Icon={BarChart3} />
        <Stat
          label="Profile views"
          value={totals.views}
          Icon={Eye}
          tone="gold"
        />
        <Stat
          label="Favourites"
          value={totals.favorites}
          Icon={Heart}
          tone="rose"
        />
      </div>

      {/* Search */}
      <div className="mt-8 relative max-w-sm">
        <Search
          size={14}
          strokeWidth={1.6}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ld-ink-muted)]"
        />
        <input
          data-testid="vendor-stats-search"
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search vendors…"
          className="w-full h-11 bg-transparent border border-[var(--ld-ink)]/25 pl-9 pr-3 text-sm text-[var(--ld-ink)] placeholder:text-[var(--ld-ink-muted)] focus:outline-none focus:border-[var(--ld-gold-deep)] transition-colors"
        />
      </div>

      {/* Table */}
      <div className="mt-6 border border-[var(--ld-border)]/80 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="vendor-stats-table">
            <thead className="bg-[var(--ld-ink)]/[0.03] text-[10px] tracking-[0.18em] uppercase text-[var(--ld-ink-muted)]">
              <tr>
                <th className="text-left font-accent font-normal px-4 py-3">
                  Vendor
                </th>
                <th className="text-right font-accent font-normal px-4 py-3">
                  Impressions
                </th>
                <th className="text-right font-accent font-normal px-4 py-3">
                  Profile views
                </th>
                <th className="text-right font-accent font-normal px-4 py-3">
                  Favourites
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--ld-border)]/70">
              {loading && (
                <tr>
                  <td
                    colSpan={4}
                    className="px-4 py-10 text-center text-[var(--ld-ink-muted)]"
                  >
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="px-4 py-10 text-center text-[var(--ld-ink-muted)] italic"
                  >
                    {rows.length === 0
                      ? "No vendor activity yet."
                      : "No matches."}
                  </td>
                </tr>
              )}
              {filtered.map((r) => (
                <tr
                  key={r.slug}
                  data-testid={`vendor-stats-row-${r.slug}`}
                  className="hover:bg-[var(--ld-ink)]/[0.02]"
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-[var(--ld-ink)]">
                      {r.displayName}
                    </div>
                    {r.email && (
                      <div className="text-[11px] text-[var(--ld-ink-muted)]">
                        {r.email}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-[var(--ld-ink)] tabular-nums">
                    {r.impressions}
                  </td>
                  <td className="px-4 py-3 text-right text-[var(--ld-ink)] tabular-nums">
                    {r.profile_views}
                  </td>
                  <td className="px-4 py-3 text-right text-rose-500 tabular-nums">
                    {r.favorites}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default VendorStatsPanel;
